import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../i18n';
import { isCoarsePointerDevice } from '../utils/devicePointer';
import { isCapacitorNative } from '../utils/isCapacitorNative';
import { Capacitor } from '@capacitor/core';
import {
  capturePhotoFromCamera,
  loadRecentPhotoThumbnails,
  pickPhotoFromGallery,
  resolveGalleryPhotoFile,
  type GalleryThumbnail,
} from '../utils/nativeGalleryPhotos';
import styles from './ImageSourcePicker.module.css';

export type ImageSourcePickerHandle = {
  open: () => void;
};

export type ImageSourcePickerResult = {
  file: File;
  caption: string;
};

type ImageSourcePickerProps = {
  /** Called with the chosen file. Prefer `onPickComplete` when caption is enabled. */
  onFileSelected?: (file: File) => void;
  /** Called with file + caption (story creation). */
  onPickComplete?: (result: ImageSourcePickerResult) => void;
  /** Show caption field before choosing a photo (story flow). */
  allowCaption?: boolean;
  accept?: string;
};

const DEFAULT_ACCEPT = 'image/*';
const CAMERA_GRID_INDEX = 2;
const HEBREW_SCRIPT_RE = /[\u0590-\u05FF]/;

type GridItem =
  | { kind: 'camera'; key: string }
  | { kind: 'loading'; key: string }
  | { kind: 'photo'; key: string; thumbnail: GalleryThumbnail };

function buildInlinePhotoGrid(thumbnails: GalleryThumbnail[], loading: boolean): GridItem[] {
  const items: GridItem[] = [];
  if (loading) {
    for (let i = 0; i < CAMERA_GRID_INDEX; i += 1) {
      items.push({ kind: 'loading', key: `loading-${i}` });
    }
    items.push({ kind: 'camera', key: 'camera' });
    return items;
  }

  const leading = thumbnails.slice(0, CAMERA_GRID_INDEX);
  const trailing = thumbnails.slice(CAMERA_GRID_INDEX);
  leading.forEach((thumbnail) => {
    items.push({ kind: 'photo', key: thumbnail.id, thumbnail });
  });
  items.push({ kind: 'camera', key: 'camera' });
  trailing.forEach((thumbnail) => {
    items.push({ kind: 'photo', key: thumbnail.id, thumbnail });
  });
  return items;
}

export const ImageSourcePicker = forwardRef<ImageSourcePickerHandle, ImageSourcePickerProps>(
  function ImageSourcePicker(
    {
      onFileSelected,
      onPickComplete,
      allowCaption = false,
      accept = DEFAULT_ACCEPT,
    },
    ref,
  ) {
    const { t, language } = useI18n();
    const galleryInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const captionRef = useRef('');
    const [pickerOpen, setPickerOpen] = useState(false);
    const [caption, setCaption] = useState('');
    const [thumbnails, setThumbnails] = useState<GalleryThumbnail[]>([]);
    const [loading, setLoading] = useState(false);
    const [resolvingId, setResolvingId] = useState<string | null>(null);
    const textDir = language === 'he' ? 'rtl' : 'ltr';
    const captionRtl = HEBREW_SCRIPT_RE.test(caption) || language === 'he';

    const closePicker = useCallback(() => {
      setPickerOpen(false);
      setThumbnails([]);
      setLoading(false);
      setResolvingId(null);
      setCaption('');
      captionRef.current = '';
    }, []);

    const deliverFile = useCallback(
      (file: File) => {
        const nextCaption = allowCaption ? captionRef.current.trim() : '';
        closePicker();
        if (onPickComplete) {
          onPickComplete({ file, caption: nextCaption });
          return;
        }
        onFileSelected?.(file);
      },
      [allowCaption, closePicker, onFileSelected, onPickComplete],
    );

    const onCaptionChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;
      captionRef.current = value;
      setCaption(value);
    }, []);

    const onInputChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        event.target.value = '';
        if (file) {
          deliverFile(file);
        }
      },
      [deliverFile],
    );

    const openGalleryInput = useCallback(() => {
      galleryInputRef.current?.click();
    }, []);

    const openCameraInput = useCallback(() => {
      cameraInputRef.current?.click();
    }, []);

    const openNativeCamera = useCallback(async () => {
      const file = await capturePhotoFromCamera();
      if (file) {
        deliverFile(file);
        return;
      }
      openCameraInput();
    }, [deliverFile, openCameraInput]);

    const onBrowseGallery = useCallback(async () => {
      if (isCapacitorNative()) {
        const file = await pickPhotoFromGallery();
        if (file) {
          deliverFile(file);
          return;
        }
      }
      openGalleryInput();
    }, [deliverFile, openGalleryInput]);

    const onCameraTap = useCallback(() => {
      if (isCapacitorNative()) {
        void openNativeCamera();
        return;
      }
      openCameraInput();
    }, [openCameraInput, openNativeCamera]);

    const onThumbnailTap = useCallback(
      async (thumbnail: GalleryThumbnail) => {
        if (resolvingId) {
          return;
        }
        setResolvingId(thumbnail.id);
        try {
          const file = await resolveGalleryPhotoFile(thumbnail.id);
          if (file) {
            deliverFile(file);
            return;
          }
        } finally {
          setResolvingId(null);
        }
        openGalleryInput();
      },
      [deliverFile, openGalleryInput, resolvingId],
    );

    useEffect(() => {
      if (!pickerOpen) {
        return;
      }
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          closePicker();
        }
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }, [pickerOpen, closePicker]);

    useEffect(() => {
      if (!pickerOpen || !isCapacitorNative()) {
        return;
      }
      let cancelled = false;
      setLoading(true);
      void loadRecentPhotoThumbnails().then((items) => {
        if (cancelled) {
          return;
        }
        setThumbnails(items);
        setLoading(false);
      });
      return () => {
        cancelled = true;
      };
    }, [pickerOpen]);

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          // Story flow always shows the sheet (caption + camera/gallery).
          if (allowCaption || isCoarsePointerDevice()) {
            setPickerOpen(true);
            return;
          }
          openGalleryInput();
        },
      }),
      [allowCaption, openGalleryInput],
    );

    const canShowInlinePhotos =
      isCapacitorNative() && Capacitor.getPlatform() === 'ios' && (loading || thumbnails.length > 0);
    const gridItems = canShowInlinePhotos ? buildInlinePhotoGrid(thumbnails, loading) : null;

    const picker =
      pickerOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              className={styles.overlay}
              role="dialog"
              aria-modal="true"
              aria-label={t('imagePicker.addToStory')}
              dir={textDir}
            >
              <header className={styles.header}>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={closePicker}
                  aria-label={t('common.close')}
                >
                  ×
                </button>
                <h2 className={styles.headerTitle}>{t('imagePicker.addToStory')}</h2>
                <span className={styles.headerSpacer} aria-hidden />
              </header>

              <div className={styles.gridScroll}>
                {gridItems ? (
                  <div className={styles.grid}>
                    {gridItems.map((item) => {
                      if (item.kind === 'camera') {
                        return (
                          <button
                            key={item.key}
                            type="button"
                            className={styles.cameraCell}
                            onClick={onCameraTap}
                            aria-label={t('imagePicker.camera')}
                          >
                            <CameraIcon />
                          </button>
                        );
                      }

                      if (item.kind === 'loading') {
                        return (
                          <div
                            key={item.key}
                            className={styles.loadingCell}
                            aria-busy="true"
                            aria-label={t('imagePicker.loading')}
                          >
                            …
                          </div>
                        );
                      }

                      return (
                        <button
                          key={item.key}
                          type="button"
                          className={styles.photoCell}
                          onClick={() => void onThumbnailTap(item.thumbnail)}
                          disabled={resolvingId === item.thumbnail.id}
                          aria-label={t('imagePicker.choosePhoto')}
                        >
                          <img
                            src={item.thumbnail.previewSrc}
                            alt=""
                            className={styles.photoThumb}
                          />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.choiceRow}>
                    <button
                      type="button"
                      className={styles.galleryChoice}
                      onClick={() => void onBrowseGallery()}
                    >
                      <GalleryIcon />
                      <span className={styles.choiceLabel}>{t('imagePicker.gallery')}</span>
                    </button>
                    <button type="button" className={styles.cameraChoice} onClick={onCameraTap}>
                      <CameraIcon />
                      <span className={styles.choiceLabel}>{t('imagePicker.camera')}</span>
                    </button>
                  </div>
                )}
              </div>

              {allowCaption ? (
                <div className={styles.captionBlock}>
                  <label className={styles.captionLabel} htmlFor="story-picker-caption">
                    {t('imagePicker.captionLabel')}
                  </label>
                  <textarea
                    id="story-picker-caption"
                    className={[styles.captionInput, captionRtl ? styles.captionInputRtl : '']
                      .filter(Boolean)
                      .join(' ')}
                    dir={captionRtl ? 'rtl' : 'ltr'}
                    value={caption}
                    onChange={onCaptionChange}
                    placeholder={t('imagePicker.captionPlaceholder')}
                    rows={2}
                    maxLength={500}
                  />
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null;

    return (
      <>
        <input
          ref={galleryInputRef}
          type="file"
          accept={accept}
          className={styles.hiddenInput}
          onChange={onInputChange}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept={accept}
          capture="environment"
          className={styles.hiddenInput}
          onChange={onInputChange}
        />
        {picker}
      </>
    );
  },
);

function CameraIcon() {
  return (
    <svg
      viewBox="0 0 48 48"
      width="28"
      height="28"
      aria-hidden
      className={styles.cameraIcon}
      fill="none"
    >
      <path
        d="M16.5 14.5 18.8 11h10.4l2.3 3.5H35a4 4 0 0 1 4 4V34a4 4 0 0 1-4 4H13a4 4 0 0 1-4-4V18.5a4 4 0 0 1 4-4h3.5Z"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <circle cx="24" cy="26" r="7.5" stroke="currentColor" strokeWidth="2.4" />
      <circle cx="24" cy="26" r="2.2" fill="currentColor" />
    </svg>
  );
}

function GalleryIcon() {
  return (
    <svg viewBox="0 0 48 48" width="28" height="28" aria-hidden fill="none">
      <rect
        x="9"
        y="12"
        width="30"
        height="24"
        rx="4"
        stroke="currentColor"
        strokeWidth="2.4"
      />
      <circle cx="18" cy="22" r="3" stroke="currentColor" strokeWidth="2.2" />
      <path
        d="M9 32.5 17 26l6 5 7-8 9 9.5"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
